import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { Save, Plus, Trash2, ArrowRight, Move } from 'lucide-react';
import './TemplateEditor.css';

const FIELD_TYPES = [
  { value: 'text', label: 'טקסט' },
  { value: 'date', label: 'תאריך' },
  { value: 'number', label: 'מספר' }
];

export default function TemplateEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState(null);
  const [pageW, setPageW] = useState(595);
  const [pageH, setPageH] = useState(842);
  const [fields, setFields] = useState([]);
  const [selectedField, setSelectedField] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newTemplateId, setNewTemplateId] = useState(null);
  const [, forceRender] = useState(0);
  const imgRef = useRef(null);
  const dragRef = useRef(null);

  const effId = id || newTemplateId;

  useEffect(() => {
    if (id) {
      api.get(`/templates/${id}`).then(res => {
        setName(res.data.name);
        setFields(res.data.fields || []);
        setPageW(res.data.page_width || 595);
        setPageH(res.data.page_height || 842);
        if (res.data.image_path) setImageUrl(`/uploads/${res.data.image_path}`);
      });
    }
  }, [id]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('name', name.trim() || 'תבנית ללא שם');
      formData.append('fields', '[]');
      const res = await api.post('/templates', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewTemplateId(res.data.id);
      setPageW(res.data.page_width || 595);
      setPageH(res.data.page_height || 842);
      if (res.data.image_path) setImageUrl(`/uploads/${res.data.image_path}`);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.error || 'שגיאה בהעלאת התמונה');
    } finally {
      setUploading(false);
    }
  };

  const addField = () => {
    const newField = {
      id: Date.now().toString(),
      label: 'שדה חדש',
      type: 'text',
      x: Math.round(pageW / 2 - 40),
      y: Math.round(pageH / 2),
      fontSize: 14,
      required: true,
      page: 0
    };
    setFields(f => [...f, newField]);
    setSelectedField(newField.id);
  };

  const updateField = (fid, updates) => {
    setFields(f => f.map(x => x.id === fid ? { ...x, ...updates } : x));
  };

  const removeField = (fid) => {
    setFields(f => f.filter(x => x.id !== fid));
    if (selectedField === fid) setSelectedField(null);
  };

  const getScale = () => {
    if (!imgRef.current) return 1;
    return imgRef.current.clientWidth / pageW;
  };

  const onMarkerMouseDown = (e, field) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedField(field.id);
    const scale = getScale();
    const rect = imgRef.current.getBoundingClientRect();
    dragRef.current = {
      id: field.id,
      offsetX: (e.clientX - rect.left) - field.x * scale,
      offsetY: (e.clientY - rect.top) - field.y * scale
    };
  };

  const onMouseMove = useCallback((e) => {
    if (!dragRef.current || !imgRef.current) return;
    const scale = imgRef.current.clientWidth / pageW;
    const rect = imgRef.current.getBoundingClientRect();
    let x = ((e.clientX - rect.left) - dragRef.current.offsetX) / scale;
    let y = ((e.clientY - rect.top) - dragRef.current.offsetY) / scale;
    x = Math.max(0, Math.min(x, pageW));
    y = Math.max(0, Math.min(y, pageH));
    updateField(dragRef.current.id, { x: Math.round(x), y: Math.round(y) });
  }, [pageW, pageH]);

  const onMouseUp = useCallback(() => { dragRef.current = null; }, []);

  useEffect(() => {
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  const handleSave = async () => {
    if (!effId) return alert('יש להעלות תמונה קודם');
    if (!name.trim()) {
      alert('יש לתת שם לתבנית לפני השמירה');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/templates/${effId}/fields`, { fields, name: name.trim() });
      navigate('/templates');
    } catch (err) {
      console.error(err);
      alert('שגיאה בשמירה');
    } finally {
      setSaving(false);
    }
  };

  const activeField = fields.find(f => f.id === selectedField);
  const scale = getScale();

  return (
    <div>
      <div className="page-header" style={{display:'flex', alignItems:'center', justifyContent:'space-between'}}>
        <div style={{display:'flex', alignItems:'center', gap:12}}>
          <button className="btn btn-secondary" onClick={() => navigate('/templates')}>
            <ArrowRight size={16} />
          </button>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="הקלד שם לתבנית..."
            style={{fontSize:20, fontWeight:700, border: name.trim() ? '1px solid transparent' : '1px solid var(--danger)', borderRadius:8, padding:'6px 12px', background: name.trim() ? 'transparent' : '#fef2f2', outline:'none', fontFamily:'Heebo', minWidth:260}}
          />
        </div>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving || !effId}>
          <Save size={16} /> {saving ? 'שומר...' : 'שמירה'}
        </button>
      </div>

      <div className="editor-layout">
        <div className="fields-panel">
          <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16}}>
            <h3 style={{fontSize:14, fontWeight:600}}>שדות</h3>
            <button className="btn btn-primary" onClick={addField} style={{padding:'6px 12px', fontSize:12}} disabled={!imageUrl}>
              <Plus size={14} /> הוסף
            </button>
          </div>

          {fields.length === 0 && (
            <p style={{fontSize:12, color:'var(--text-muted)', textAlign:'center', padding:'12px 0'}}>
              {imageUrl ? 'לחץ "הוסף" ואז גרור את השדה למקום' : 'העלה תמונה כדי להתחיל'}
            </p>
          )}

          {fields.map(field => (
            <div
              key={field.id}
              className={`field-item ${selectedField === field.id ? 'active' : ''}`}
              onClick={() => setSelectedField(field.id)}
            >
              <span style={{fontSize:13, fontWeight:500}}>{field.label}</span>
              <button onClick={(e) => { e.stopPropagation(); removeField(field.id); }} style={{background:'none', border:'none', cursor:'pointer', color:'var(--danger)'}}>
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          {activeField && (
            <div className="field-editor">
              <h4 style={{fontSize:13, fontWeight:600, marginBottom:12}}>עריכת שדה</h4>
              <div className="form-group" style={{marginBottom:10}}>
                <label className="label" style={{fontSize:12}}>שם השדה</label>
                <input className="input" value={activeField.label} onChange={e => updateField(activeField.id, {label: e.target.value})} style={{fontSize:13}} />
              </div>
              <div className="form-group" style={{marginBottom:10}}>
                <label className="label" style={{fontSize:12}}>סוג</label>
                <select className="input" value={activeField.type} onChange={e => updateField(activeField.id, {type: e.target.value})} style={{fontSize:13}}>
                  {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="label" style={{fontSize:12}}>גודל פונט</label>
                <input className="input" type="number" value={activeField.fontSize} onChange={e => updateField(activeField.id, {fontSize: +e.target.value})} style={{fontSize:13}} />
              </div>
              <p style={{fontSize:11, color:'var(--text-muted)', marginTop:10, display:'flex', alignItems:'center', gap:4}}>
                <Move size={12} /> גרור את השדה על התבנית
              </p>
            </div>
          )}
        </div>

        <div className="pdf-area">
          {uploading ? (
            <div className="pdf-upload-zone">
              <div style={{textAlign:'center'}}>
                <div style={{fontWeight:600, fontSize:16}}>מעלה...</div>
              </div>
            </div>
          ) : !imageUrl ? (
            <div className="pdf-upload-zone">
              <input type="file" accept="image/png,image/jpeg" onChange={handleImageUpload} id="img-upload" style={{display:'none'}} />
              <label htmlFor="img-upload" style={{cursor:'pointer', textAlign:'center'}}>
                <div style={{fontWeight:600, marginBottom:4, fontSize:16}}>העלה תמונת בלנק</div>
                <div style={{fontSize:13, color:'var(--text-muted)'}}>PNG או JPG</div>
                <div style={{fontSize:12, color:'var(--text-muted)', marginTop:8}}>טיפ: המר את ה-PDF לתמונה (צילום מסך או אתר המרה)</div>
              </label>
            </div>
          ) : (
            <div className="pdf-scroll">
              <div className="img-container">
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="template"
                  className="template-img"
                  draggable={false}
                  onLoad={() => forceRender(n => n + 1)}
                />
                <div className="drag-layer">
                  {fields.map(field => (
                    <div
                      key={field.id}
                      className={`field-marker ${selectedField === field.id ? 'selected' : ''}`}
                      style={{
                        left: field.x * scale,
                        top: field.y * scale,
                        fontSize: Math.max(9, field.fontSize * scale)
                      }}
                      onMouseDown={(e) => onMarkerMouseDown(e, field)}
                    >
                      {field.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
